import { useState } from "react";
import { Menu, X, PhoneCall } from "lucide-react";
import Container from "./ui/Container";
import { PrimaryButton } from "./ui/Buttons";
import { href, useLocation, useNavigate } from "react-router-dom";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Unified state for desktop dropdowns
    const [dropdownsOpen, setDropdownsOpen] = useState<Record<string, boolean>>({});

    // Mobile dropdown states
    const [mobileDropdowns, setMobileDropdowns] = useState<Record<string, boolean>>({});

    const navItems = [
        { label: "Home", href: "/" },
        {
            label: "Loans", href: "/loans",
            dropdown: [
                { label: "Personal Loan", href: "/loans" },
                { label: "Home Loan", href: "/loans" },
                { label: "Business Loan", href: "/loans" },
                { label: "Car Loan", href: "/loans" },
                { label: "Educational Loan", href: "/loans" },
                { label: "LAP", href: "/loans" },
            ],
        },
        {
            label: "Insurance", href: "/insurance",
            dropdown: [
                { label: "Life Insurance (LIC)", href: "/insurance" },
                { label: "Health Insurance (Star Health)", href: "/insurance" },
            ],
        },
        {
            label: "Thawa FSPL", href: "/thawa",
            dropdown: [
                { label: "Home Loan", href: "/thawa" },
                { label: "Business Loan", href: "/thawa" },
                { label: "Personal Loan", href: "/thawa" },
            ],
        },
        {
            label: "Real Estate", href: "/realestate",
            dropdown: [
                { label: "Residential", href: "/realestate" },
                { label: "Commercial", href: "/realestate" },
            ],
        },
        { label: "About", href: "/about" },
    ];

    const handleClick = (href: string) => {
        if (href.startsWith("/")) {
            if (location.pathname === href) {
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                navigate(href);
            }
        } else {
            const el = document.querySelector(href);
            el?.scrollIntoView({ behavior: "smooth" });
        }
        setOpen(false);
    };

    const toggleMobileDropdown = (label: string) => {
        setMobileDropdowns((prev) => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const handleDropdown = (label: string, value: boolean) => {
        setDropdownsOpen((prev) => ({ ...prev, [label]: value }));
    };

    const isOpen = (label: string) => !!dropdownsOpen[label];

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b bg-white/90 backdrop-blur">
            <Container className="flex h-16 items-center justify-between">
                {/* Site Name */}
                <button
                    onClick={() => {
                        if (location.pathname === "/") {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        } else {
                            navigate("/");
                        }
                    }}
                    className="text-lg font-extrabold tracking-tight text-gray-900 hover:text-blue-700 transition-colors duration-300"
                >
                    Pollachi Wealth Care
                </button>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-6 relative">
                    {navItems.map((item) =>
                        item.dropdown ? (
                            <div
                                key={item.label}
                                className="relative"
                                onMouseEnter={() => handleDropdown(item.label, true)}
                                onMouseLeave={() => handleDropdown(item.label, false)}
                            >
                                <button onClick={() => handleClick(item.href)} className="relative text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors duration-300">
                                    {item.label}
                                </button>
                                {/* Dropdown Menu */}
                                <div
                                    className={`absolute top-full left-0 mt-1 bg-white shadow-md rounded-lg py-2 w-48 transition-opacity duration-200 ${isOpen(item.label) ? "opacity-100 visible" : "opacity-0 invisible"
                                        }`}
                                >
                                    {item.dropdown.map((d) => (
                                        <button
                                            key={d.href}
                                            onClick={() => handleClick(d.href)}
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
                                        >
                                            {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <button
                                key={item.href}
                                onClick={() => handleClick(item.href)}
                                className="relative text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors duration-300"
                            >
                                {item.label}
                            </button>
                        )
                    )}

                    <PrimaryButton
                        onClick={() => navigate("/contact")}
                        className="ml-4 transform active:scale-95 transition-transform duration-150"
                    >
                        <PhoneCall className="h-4 w-4 mr-2" /> Let's Talk
                    </PrimaryButton>
                </nav>

                {/* Mobile Toggle */}
                <button
                    className="md:hidden p-2 transition-transform active:scale-95"
                    onClick={() => setOpen(!open)}
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </Container>

            {/* Mobile Menu */}
            {open && (
                <nav className="md:hidden flex flex-col gap-2 p-4 bg-white border-t shadow-md text-left">
                    {navItems.map((item) =>
                        item.dropdown ? (
                            <div key={item.label} className="flex flex-col">
                                <button
                                    className="font-medium text-gray-700 flex justify-between items-center py-2 w-full text-left"
                                    onClick={() => toggleMobileDropdown(item.label)}
                                >
                                    {item.label}
                                    <span className="ml-2">{mobileDropdowns[item.label] ? "▲" : "▼"}</span>
                                </button>
                                {mobileDropdowns[item.label] && (
                                    <div className="flex flex-col pl-4">
                                        {item.dropdown.map((d) => (
                                            <button
                                                key={d.href}
                                                onClick={() => handleClick(d.href)}
                                                className="text-gray-700 py-1 hover:text-blue-700 text-left w-full"
                                            >
                                                {d.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                key={item.href}
                                onClick={() => handleClick(item.href)}
                                className="text-gray-700 font-medium hover:text-blue-700 transition-colors duration-300 py-2 w-full text-left"
                            >
                                {item.label}
                            </button>
                        )
                    )}

                    <PrimaryButton
                        onClick={() => {
                            navigate("/contact");
                            setOpen(false);
                        }}
                        className="mt-2 w-full justify-start"
                    >
                        <PhoneCall className="h-4 w-4 mr-2" /> Let's Talk
                    </PrimaryButton>
                </nav>
            )}
        </header>
    );
}
